import { useEffect, useRef } from 'preact/hooks';
import * as THREE from 'three';
import { decodeSkin } from '@vm/content';
import { previewRenderer, type PreviewHandle, type PreviewScene } from './previewRenderer';
import { Spin } from './spin';
import { addStudioLights, makePedestal, makeShadowBlob } from './stage';
import { IdlePose, VoxelMarine } from './voxelCharacter';
import '../../styles/three-ui.css';

/**
 * 3D preview of a Voxel Marine (skin + armor + held weapon) for menus.
 * Owned by the 3D-UI workstream; this is the interface contract.
 */
export interface CharacterPreviewProps {
  /** Base64 skin data or 'preset:<id>'. */
  skin: string;
  armor: string;
  /** Weapon id to hold (optional). */
  weapon?: string;
  /** Camo id for the held weapon (optional). */
  camo?: string;
  /** Slowly auto-rotate; the user can always drag to rotate. */
  rotate?: boolean;
  className?: string;
  /** Core tint: -1 neutral (default), 0 team A, 1 team B. */
  team?: number;
  /** Show the plinth under the marine (default true). */
  pedestal?: boolean;
  /** Allow drag-to-rotate (default true). */
  interactive?: boolean;
  /** Starting turn in radians (default: a slight three-quarter view). */
  initialYaw?: number;
}

const TARGET_Y = 0.78;

/** Scene for one character preview; drawn by the shared preview renderer. */
class CharacterStage implements PreviewScene {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(24, 1, 0.1, 40);
  readonly spin: Spin;
  private readonly turn = new THREE.Group();
  private readonly pedestal: THREE.Group;
  private marine: VoxelMarine | null = null;
  private readonly idle = new IdlePose();
  private key = '';
  private weaponId: string | undefined;
  private weaponCamo: string | undefined;

  constructor(initialYaw: number) {
    this.spin = new Spin(initialYaw);
    addStudioLights(this.scene);
    this.pedestal = makePedestal(0.6);
    this.turn.add(this.pedestal, makeShadowBlob(1.0, 0.8, 0.9));
    this.scene.add(this.turn);
    this.resize(1, 1);
  }

  setPedestal(on: boolean): void {
    this.pedestal.visible = on;
  }

  setCharacter(skin: string, armor: string, team: number): void {
    const key = `${skin}\n${armor}\n${team}`;
    if (key === this.key) return;
    this.key = key;
    const next = new VoxelMarine(decodeSkin(skin), armor, team);
    next.setWeapon(this.weaponId, this.weaponCamo);
    if (this.marine) {
      this.turn.remove(this.marine.root);
      this.marine.dispose();
    }
    this.marine = next;
    this.turn.add(next.root);
  }

  setWeapon(id: string | undefined, camo: string | undefined): void {
    this.weaponId = id;
    this.weaponCamo = camo;
    this.marine?.setWeapon(id, camo);
  }

  tick(dt: number): boolean {
    this.spin.tick(dt);
    this.turn.rotation.y = this.spin.yaw;
    if (this.marine) this.idle.update(this.marine, dt);
    return true;
  }

  resize(w: number, h: number): void {
    const aspect = w / Math.max(1, h);
    const cam = this.camera;
    cam.aspect = aspect;
    const t = Math.tan(THREE.MathUtils.degToRad(cam.fov / 2));
    // Fit the marine + plinth (about 1.9 m tall, 1.4 m wide incl. a held weapon).
    const dist = Math.max(1.1 / t, 0.76 / (t * aspect));
    const elev = 0.13;
    cam.position.set(0, TARGET_Y + Math.sin(elev) * dist, -Math.cos(elev) * dist);
    cam.lookAt(0, TARGET_Y, 0);
    cam.updateProjectionMatrix();
  }

  dispose(): void {
    this.marine?.dispose();
    this.marine = null;
  }
}

export function CharacterPreview(props: CharacterPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<CharacterStage | null>(null);
  const handleRef = useRef<PreviewHandle | null>(null);

  if (!stageRef.current) stageRef.current = new CharacterStage(props.initialYaw ?? -0.42);
  const stage = stageRef.current;

  useEffect(() => {
    const canvas = canvasRef.current!;
    const handle = previewRenderer.attach(canvas, stage);
    handleRef.current = handle;
    return () => {
      handle.detach();
      handleRef.current = null;
      stage.dispose();
      stageRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (props.interactive === false) return;
    return stage.spin.attach(canvasRef.current!, (on) => handleRef.current?.setInteracting(on));
  }, [props.interactive]);

  useEffect(() => {
    stage.setCharacter(props.skin, props.armor, props.team ?? -1);
    handleRef.current?.invalidate();
  }, [props.skin, props.armor, props.team]);

  useEffect(() => {
    stage.setWeapon(props.weapon, props.camo);
    handleRef.current?.invalidate();
  }, [props.weapon, props.camo]);

  useEffect(() => {
    stage.spin.auto = props.rotate ? 0.45 : 0;
    stage.setPedestal(props.pedestal !== false);
    handleRef.current?.invalidate();
  }, [props.rotate, props.pedestal]);

  return (
    <div class={`char-preview three-preview ${props.className ?? ''}`}>
      <canvas ref={canvasRef} class={`three-canvas${props.interactive === false ? '' : ' spinnable'}`} />
    </div>
  );
}
